/**
 * useStudentAnalytics
 *
 * Aggregates all stored student data into chart-ready structures.
 * Used by StudentInsights (self-view), ParentPortal (read-only), and Analytics.jsx (admin).
 *
 * All computation is synchronous — reads from StorageService (localStorage).
 */

import { StorageService } from '@/hooks/services/storageService';

export interface TraitPoint {
  week: string;   // e.g. "Week 1"
  [traitName: string]: number | string;
}

export interface ActivityBreakdown {
  name: string;
  value: number;
  fill: string;
}

export interface SubjectStrength {
  subject: string;
  score: number;
}

export interface GoalProgressData {
  active: number;
  completed: number;
  overdue: number;
}

export interface EngagementPoint {
  week: string;
  sessions: number;
}

export interface CareerPathProgress {
  pathId: string;
  pathName: string;
  progress: number;
}

export interface WeeklyXP {
  week: string;
  xp: number;
}

export interface StudentAnalytics {
  traitHistory: TraitPoint[];
  activityBreakdown: ActivityBreakdown[];
  subjectStrengths: SubjectStrength[];
  goalProgress: GoalProgressData;
  engagementTrend: EngagementPoint[];
  careerPathProgress: CareerPathProgress[];
  topAchievements: any[];
  weeklyXP: WeeklyXP[];
  inferredStrengths: string[];
  inferredInterests: string[];
}

// ─── Colour palette for charts ────────────────────────────────────────────────
const CHART_COLORS = ['#6366f1', '#14b8a6', '#f59e0b', '#ec4899', '#10b981', '#8b5cf6', '#f97316']

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isoWeekLabel(dateStr: string): string {
  const d = new Date(dateStr)
  const start = new Date(d)
  start.setDate(d.getDate() - d.getDay()) // Sunday
  return start.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

function binByWeek<T extends { timestamp: string }>(items: T[]): Map<string, T[]> {
  const map = new Map<string, T[]>()
  for (const item of items) {
    const key = isoWeekLabel(item.timestamp)
    if (!map.has(key)) map.set(key, [])
    map.get(key)!.push(item)
  }
  return map
}

const TRAIT_DISPLAY: Record<string, string> = {
  T01: 'Critical Thinking', T02: 'Analytical Reasoning', T03: 'Creativity',
  T04: 'Numerical Ability', T05: 'Problem Solving', T06: 'Technical Aptitude',
  T17: 'Communication', T19: 'Teamwork', T22: 'Writing',
  T31: 'Self-Discipline', T33: 'Leadership', T48: 'Academic Drive',
}
function traitName(id: string): string { return TRAIT_DISPLAY[id] ?? id }

// ─── Main export ──────────────────────────────────────────────────────────────

export function useStudentAnalytics(studentId: string): StudentAnalytics {
  const students: any[] = StorageService.read('students')
  const student = students.find((s) => s._id === studentId || s.userId === studentId)

  if (!student) {
    return {
      traitHistory: [], activityBreakdown: [], subjectStrengths: [],
      goalProgress: { active: 0, completed: 0, overdue: 0 },
      engagementTrend: [], careerPathProgress: [], topAchievements: [],
      weeklyXP: [], inferredStrengths: [], inferredInterests: [],
    }
  }

  // ── Trait history (from traitEvents, binned by week) ──────────────────────
  const traitEvents: any[] = student.traitEvents ?? []
  const topTraitIds = Object.entries((student.traitProfile ?? {}) as Record<string, number>)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4)
    .map(([id]) => id)

  const eventsByWeek = binByWeek(traitEvents)
  const traitHistory: TraitPoint[] = Array.from(eventsByWeek.entries())
    .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
    .map(([week, events]) => {
      const point: TraitPoint = { week }
      for (const id of topTraitIds) {
        const deltas = events.flatMap((e: any) => e.traitDeltas?.[id] ?? [])
        point[traitName(id)] = Math.round(deltas.reduce((s, d) => s + d, 0))
      }
      return point
    })
    .slice(-8)

  // ── Activity breakdown (count by eventType category) ──────────────────────
  const activityLog: any[] = student.activityLog ?? []
  const categoryCount: Record<string, number> = {}
  for (const entry of activityLog) {
    const cat = entry.eventCategory ?? 'platform'
    categoryCount[cat] = (categoryCount[cat] ?? 0) + 1
  }
  const activityBreakdown: ActivityBreakdown[] = Object.entries(categoryCount).map(([name, value], i) => ({
    name: name === 'platform' ? 'Platform' : name === 'self_reported' ? 'Self-reported' : 'External',
    value,
    fill: CHART_COLORS[i % CHART_COLORS.length],
  }))

  // ── Subject strengths (avg outcomeScore per subjectArea) ──────────────────
  const subjectMap: Record<string, { total: number; count: number }> = {}
  for (const entry of activityLog) {
    if (entry.subjectArea && entry.outcomeScore != null) {
      if (!subjectMap[entry.subjectArea]) subjectMap[entry.subjectArea] = { total: 0, count: 0 }
      subjectMap[entry.subjectArea].total += entry.outcomeScore
      subjectMap[entry.subjectArea].count++
    }
  }
  const subjectStrengths: SubjectStrength[] = Object.entries(subjectMap)
    .map(([subject, { total, count }]) => ({ subject, score: Math.round(total / count) }))
    .sort((a, b) => b.score - a.score)

  // ── Goal progress ─────────────────────────────────────────────────────────
  const allGoals: any[] = StorageService.read('goals')
  const myGoals = allGoals.filter((g) => g.userId === student.userId || g.studentId === student._id)
  const now = Date.now()
  const goalProgress: GoalProgressData = {
    completed: myGoals.filter((g) => g.status === 'completed').length,
    active: myGoals.filter((g) => g.status !== 'completed' && (!g.dueDate || new Date(g.dueDate).getTime() >= now)).length,
    overdue: myGoals.filter((g) => g.status !== 'completed' && g.dueDate && new Date(g.dueDate).getTime() < now).length,
  }

  // ── Engagement trend (sessions per week, last 8 weeks) ───────────────────
  const sessionEvents = activityLog.filter((e) => e.eventType === 'session_start')
  const sessionsByWeek = binByWeek(sessionEvents)
  const engagementTrend: EngagementPoint[] = Array.from(sessionsByWeek.entries())
    .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
    .map(([week, events]) => ({ week, sessions: events.length }))
    .slice(-8)

  // ── Career path progress (from journey records) ───────────────────────────
  const journeyRecords: any[] = StorageService.read('journeyRecords')
  const myJourneys = journeyRecords.filter((j) => j.userId === student.userId)
  const careerPathProgress: CareerPathProgress[] = myJourneys.map((j) => ({
    pathId: j.pathId,
    pathName: j.pathName ?? j.pathId,
    progress: j.progressPercent ?? 0,
  }))

  // ── Top achievements ──────────────────────────────────────────────────────
  const allAchievements: any[] = StorageService.read('achievements')
  const topAchievements = allAchievements
    .filter((a) => a.userId === student.userId && a.unlockedAt)
    .sort((a, b) => new Date(b.unlockedAt).getTime() - new Date(a.unlockedAt).getTime())
    .slice(0, 5)

  // ── Weekly XP (from traitEvents — sum |delta| per week as proxy for XP) ──
  const xpByWeek = binByWeek(traitEvents)
  const weeklyXP: WeeklyXP[] = Array.from(xpByWeek.entries())
    .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
    .map(([week, events]) => ({
      week,
      xp: Math.round(
        events.reduce((sum, e) => {
          const deltas = Object.values(e.traitDeltas ?? {}) as number[]
          return sum + deltas.reduce((s, d) => s + Math.abs(d), 0)
        }, 0) * 10,
      ),
    }))
    .slice(-8)

  // ── Inferred strengths / interests ───────────────────────────────────────
  const profile = student.traitProfile ?? {}
  const inferredStrengths = Object.entries(profile as Record<string, number>)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([id]) => traitName(id))

  const topicCount: Record<string, number> = {}
  for (const entry of activityLog) {
    if (entry.topicFamilyId) topicCount[entry.topicFamilyId] = (topicCount[entry.topicFamilyId] ?? 0) + 1
  }
  const inferredInterests = Object.entries(topicCount)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4)
    .map(([id]) => id.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()))

  return {
    traitHistory, activityBreakdown, subjectStrengths,
    goalProgress, engagementTrend, careerPathProgress,
    topAchievements, weeklyXP, inferredStrengths, inferredInterests,
  }
}
